import { useEffect, useState } from "react";
import type { ProgressEvent } from "../hooks/useSSE";

function fmtDur(ms: number): string {
  if (ms < 0 || !Number.isFinite(ms)) return "—";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${String(s % 60).padStart(2, "0")}s`;
}

/**
 * Rich live progress for a running job (apply / sync): a bar plus processed/total actions, failures,
 * an estimated API-call count, elapsed time and a rolling ETA. Self-ticks every second so elapsed/ETA
 * stay live even between SSE events. Works for a background job too (the event is the server's
 * last-known progress, which survives a page reload).
 */
export function JobProgress({ ev, compact }: { ev: ProgressEvent; compact?: boolean }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const error = ev.percent < 0;
  const pct = error ? 100 : Math.max(0, Math.min(100, ev.percent));
  const { total, processed, failed, apiEstimate, startedAt } = ev;
  const elapsed = startedAt ? now - startedAt : null;

  let etaMs: number | null = null;
  if (elapsed != null && elapsed > 1500 && !error) {
    if (total && processed && processed > 0 && processed < total) etaMs = (elapsed / processed) * (total - processed);
    else if (pct > 2 && pct < 100) etaMs = (elapsed * (100 - pct)) / pct;
  }

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5 text-xs">
        <span className="min-w-0 truncate font-medium text-slate-700">{ev.step}{ev.detail ? ` — ${ev.detail}` : ""}</span>
        <span className="shrink-0 tabular-nums text-slate-500">{error ? "error" : `${pct}%`}</span>
      </div>
      <div className={`${compact ? "h-1.5" : "h-2"} w-full overflow-hidden rounded-full bg-slate-100`}>
        <div className={`h-full transition-all ${error ? "bg-red-500" : "bg-indigo-600"}`} style={{ width: `${Math.max(3, pct)}%` }} />
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] tabular-nums text-slate-500">
        {total != null && (
          <span><span className="font-semibold text-slate-700">{(processed ?? 0).toLocaleString("nl-NL")}/{total.toLocaleString("nl-NL")}</span> actions</span>
        )}
        {failed ? <span className="font-medium text-red-600">{failed} failed</span> : null}
        {apiEstimate != null && apiEstimate > 0 && <span>≈ {apiEstimate.toLocaleString("nl-NL")} API calls</span>}
        {elapsed != null && <span>elapsed {fmtDur(elapsed)}</span>}
        {etaMs != null && <span>~{fmtDur(etaMs)} left</span>}
        {ev.memoryMB?.rss ? <span className="text-slate-400">{ev.memoryMB.rss} MB</span> : null}
      </div>
    </div>
  );
}
